import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DOMPurify from 'dompurify';
import { IPopupItem, IPopupSettings } from '../types/damTypes';
import { API_BASE_URL } from '../config';
import axiosInstance from '../services/axiosInstance';

interface PopupNewsModalProps {
  open: boolean;
  onClose: () => void;
}

const PopupNewsModal: React.FC<PopupNewsModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [items, setItems] = useState<IPopupItem[]>([]);
  const [settings, setSettings] = useState<IPopupSettings | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isHovering, setIsHovering] = useState(false);

  // Fetch popup items and settings
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [itemsRes, settingsRes] = await Promise.all([
          axiosInstance.get('/api/popup/items/active'),
          axiosInstance.get('/api/popup/settings')
        ]);
        setItems(itemsRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching popup data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchData();
    }
  }, [open]);

  // Auto-advance carousel (pauses on hover)
  useEffect(() => {
    if (!settings?.autoAdvanceSeconds || settings.autoAdvanceSeconds === 0) return;
    if (items.length <= 1) return;
    if (!open) return;
    if (isHovering) return; // Pause when hovering

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, settings.autoAdvanceSeconds * 1000);

    return () => clearInterval(interval);
  }, [settings?.autoAdvanceSeconds, items.length, open, isHovering]);

  const handleClose = () => {
    if (dontShowAgain && settings?.allowDismiss) {
      // Store in localStorage that user doesn't want to see this again
      localStorage.setItem('popup_dismissed', 'true');
    }
    onClose();
  };

  const handleCtaClick = (link?: string) => {
    if (link) {
      if (link.startsWith('http')) {
        window.open(link, '_blank');
      } else {
        window.location.href = link;
      }
    }
  };

  const currentItem = items[currentIndex];

  if (loading || !items.length) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isMobile ? '95vw' : 800,
          maxWidth: '95vw',
          maxHeight: '90vh',
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: '#ffffff',
          position: 'relative'
        }
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 10,
          bgcolor: 'rgba(255,255,255,0.9)',
          '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent 
        sx={{ p: 0, position: 'relative' }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {currentItem && (
          <Fade in key={currentItem._id} timeout={300}>
            <Box>
              {/* Badge with different styles */}
              <Box
                sx={{
                  position: 'absolute',
                  top: (currentItem.badgeStyle === 'corner-fold' ? -5 : 0) + (currentItem.badgeOffsetY || 0),
                  ...((currentItem.badgePosition || 'top-right') === 'top-right' 
                    ? { right: currentItem.badgeStyle === 'corner-fold' ? -5 : 0 } 
                    : { left: currentItem.badgeStyle === 'corner-fold' ? -5 : 0 }),
                  zIndex: 5,
                  transform: `scale(${currentItem.badgeScale || 1})`,
                  transformOrigin: (currentItem.badgePosition || 'top-right') === 'top-right' ? 'top right' : 'top left'
                }}
              >
                {/* Ribbon Horizontal */}
                {(currentItem.badgeStyle === 'ribbon-horizontal' || !currentItem.badgeStyle) && (
                  <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                    <Box
                      sx={{
                        bgcolor: currentItem.badgeColor || '#8B1538',
                        color: currentItem.badgeTextColor || '#FFFFFF',
                        px: 3,
                        py: 1.5,
                        fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        letterSpacing: '0.05em'
                      }}
                    >
                      {currentItem.badgeText}
                    </Box>
                    <Box
                      sx={{
                        width: 0,
                        height: 0,
                        borderTop: '22px solid transparent',
                        borderBottom: '22px solid transparent',
                        borderRight: `16px solid ${currentItem.badgeColor || '#8B1538'}`,
                        transform: 'rotate(180deg)'
                      }}
                    />
                  </Box>
                )}

                {/* Ribbon Vertical */}
                {currentItem.badgeStyle === 'ribbon-vertical' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box
                      sx={{
                        bgcolor: currentItem.badgeColor || '#8B1538',
                        color: currentItem.badgeTextColor || '#FFFFFF',
                        px: 3,
                        pt: 2,
                        pb: 3,
                        fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                        fontWeight: 700,
                        fontSize: '1.3rem',
                        letterSpacing: '0.05em',
                        textAlign: 'center',
                        minWidth: 90
                      }}
                    >
                      {currentItem.badgeText}
                    </Box>
                    <Box
                      sx={{
                        width: 0,
                        height: 0,
                        borderLeft: '45px solid ' + (currentItem.badgeColor || '#8B1538'),
                        borderRight: '45px solid ' + (currentItem.badgeColor || '#8B1538'),
                        borderBottom: '20px solid transparent',
                      }}
                    />
                  </Box>
                )}

                {/* Corner Fold */}
                {currentItem.badgeStyle === 'corner-fold' && (
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 22,
                        right: -45,
                        bgcolor: currentItem.badgeColor || '#8B1538',
                        color: currentItem.badgeTextColor || '#FFFFFF',
                        px: 5,
                        py: 0.75,
                        fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        letterSpacing: '0.05em',
                        transform: 'rotate(45deg)',
                        transformOrigin: 'center',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                      }}
                    >
                      {currentItem.badgeText}
                    </Box>
                  </Box>
                )}

                {/* Circle */}
                {currentItem.badgeStyle === 'circle' && (
                  <Box
                    sx={{
                      bgcolor: currentItem.badgeColor || '#8B1538',
                      color: currentItem.badgeTextColor || '#FFFFFF',
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      letterSpacing: '0.02em',
                      textAlign: 'center',
                      m: 1.5,
                      boxShadow: '0 3px 10px rgba(0,0,0,0.3)'
                    }}
                  >
                    {currentItem.badgeText}
                  </Box>
                )}

                {/* Tag */}
                {currentItem.badgeStyle === 'tag' && (
                  <Box
                    sx={{
                      bgcolor: currentItem.badgeColor || '#8B1538',
                      color: currentItem.badgeTextColor || '#FFFFFF',
                      px: 2.5,
                      py: 1,
                      borderRadius: '6px 0 0 6px',
                      fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                      fontWeight: 600,
                      fontSize: '1rem',
                      letterSpacing: '0.05em',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                    }}
                  >
                    {currentItem.badgeText}
                  </Box>
                )}
              </Box>

              {/* Image with title overlay */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: currentItem.bodyText?.trim() ? '16/10' : '16/12',
                  overflow: 'hidden'
                }}
              >
                <Box
                  component="img"
                  src={currentItem.imageUrl.startsWith('http') 
                    ? currentItem.imageUrl 
                    : `${API_BASE_URL}${currentItem.imageUrl}`}
                  alt={currentItem.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                
                {/* Title overlay - positioned based on titlePosition */}
                <Box
                  sx={{
                    position: 'absolute',
                    ...(currentItem.titlePosition?.includes('top') && { top: 0 }),
                    ...(currentItem.titlePosition?.includes('middle') && { top: '50%', transform: 'translateY(-50%)' }),
                    ...((currentItem.titlePosition?.includes('bottom') || !currentItem.titlePosition) && { bottom: 0 }),
                    ...(currentItem.titlePosition?.includes('left') && { left: 0, textAlign: 'left' }),
                    ...(currentItem.titlePosition?.includes('center') && { left: 0, right: 0, textAlign: 'center' }),
                    ...(currentItem.titlePosition?.includes('right') && { right: 0, textAlign: 'right' }),
                    ...((currentItem.titlePosition?.includes('bottom') || !currentItem.titlePosition) && {
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      left: 0,
                      right: 0,
                      pt: 8
                    }),
                    ...(currentItem.titlePosition?.includes('top') && {
                      background: 'linear-gradient(rgba(0,0,0,0.7), transparent)',
                      left: 0,
                      right: 0,
                      pb: 8
                    }),
                    p: 4
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      color: '#ffffff',
                      fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                      fontWeight: 600,
                      fontSize: isMobile ? `${(currentItem.titleSize || 2.5) * 0.72}rem` : `${currentItem.titleSize || 2.5}rem`,
                      textShadow: '2px 2px 8px rgba(0,0,0,0.5)',
                      lineHeight: 1.1
                    }}
                  >
                    {currentItem.title}
                  </Typography>
                </Box>

                {/* CTA overlay when no body text */}
                {!currentItem.bodyText?.trim() && !currentItem.bodyTitle?.trim() && currentItem.ctaText && currentItem.ctaLink && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 24,
                      left: '50%',
                      transform: 'translateX(-50%)'
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => handleCtaClick(currentItem.ctaLink)}
                      sx={{
                        bgcolor: currentItem.ctaButtonColor || currentItem.badgeColor || '#8B1538',
                        color: '#FFFFFF',
                        fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                        fontWeight: 600,
                        fontSize: '1rem',
                        letterSpacing: '0.05em',
                        px: 4,
                        py: 1,
                        boxShadow: '0 3px 12px rgba(0,0,0,0.4)',
                        '&:hover': {
                          bgcolor: currentItem.ctaButtonColor || currentItem.badgeColor || '#8B1538',
                          filter: 'brightness(1.1)'
                        }
                      }}
                    >
                      {currentItem.ctaText} →
                    </Button>
                  </Box>
                )}

                {/* Carousel navigation dots - overlay on image */}
                {items.length > 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      gap: 0.75,
                      zIndex: 10
                    }}
                  >
                    {items.map((_, idx) => (
                      <Box
                        key={idx}
                        onClick={() => setCurrentIndex(idx)}
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: idx === currentIndex ? '#FFFFFF' : 'rgba(255,255,255,0.5)',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Body section - if there's body text or body title */}
              {(currentItem.bodyText?.trim() || currentItem.bodyTitle?.trim()) && (
                <Box sx={{ p: 3, pb: 2, bgcolor: currentItem.bodyBackgroundColor || '#072f53' }}>
                  {/* Body Title */}
                  {currentItem.bodyTitle && (
                    <Typography
                      sx={{
                        color: currentItem.bodyTextColor || '#FFFFFF',
                        fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                        fontWeight: 700,
                        fontSize: isMobile 
                          ? `${(currentItem.bodyTitleSize || 1.6) * 0.85}rem` 
                          : `${currentItem.bodyTitleSize || 1.6}rem`,
                        mb: 2,
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em'
                      }}
                    >
                      {currentItem.bodyTitle}
                    </Typography>
                  )}

                  {/* Body text with optional floating image */}
                  {currentItem.bodyText && (
                    <Box>
                      {currentItem.bodyImagePosition && currentItem.bodyImagePosition !== 'none' && currentItem.bodyImageUrl && (
                        <Box
                          component="img"
                          src={currentItem.bodyImageUrl.startsWith('http') 
                            ? currentItem.bodyImageUrl 
                            : `${API_BASE_URL}${currentItem.bodyImageUrl}`}
                          alt="Body image"
                          sx={{
                            width: isMobile ? '45%' : '35%',
                            maxWidth: 200,
                            height: 'auto',
                            borderRadius: 1,
                            float: currentItem.bodyImagePosition,
                            ...(currentItem.bodyImagePosition === 'right' ? { ml: 2, mb: 2 } : { mr: 2, mb: 2 })
                          }}
                        />
                      )}
                      <Typography
                        variant="body1"
                        component="div"
                        sx={{
                          color: currentItem.bodyTextColor || '#FFFFFF',
                          fontSize: '1rem',
                          lineHeight: 1.7,
                          '& a': { color: 'inherit', textDecoration: 'underline' },
                          '& b, & strong': { fontWeight: 700 },
                          '& i, & em': { fontStyle: 'italic' }
                        }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentItem.bodyText || '') }}
                      />
                      <Box sx={{ clear: 'both' }} />
                    </Box>
                  )}

                  {/* CTA - text link or button */}
                  {currentItem.ctaText && currentItem.ctaLink && (
                    <Box sx={{ mt: 3 }}>
                      {(currentItem.ctaStyle === 'text-link' || !currentItem.ctaStyle) ? (
                        <Typography
                          onClick={() => handleCtaClick(currentItem.ctaLink)}
                          sx={{
                            color: currentItem.bodyTextColor || '#FFFFFF',
                            fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                            fontWeight: 700,
                            fontSize: isMobile ? '0.9rem' : '1rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.02em',
                            cursor: 'pointer',
                            display: 'inline-block',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {currentItem.ctaText}
                        </Typography>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={() => handleCtaClick(currentItem.ctaLink)}
                          sx={{
                            bgcolor: currentItem.ctaButtonColor || currentItem.badgeColor || '#8B1538',
                            color: '#FFFFFF',
                            fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                            fontWeight: 600,
                            fontSize: '1rem',
                            letterSpacing: '0.05em',
                            px: 4,
                            py: 1,
                            '&:hover': {
                              bgcolor: currentItem.ctaButtonColor || currentItem.badgeColor || '#8B1538',
                              filter: 'brightness(1.1)'
                            }
                          }}
                        >
                          {currentItem.ctaText} →
                        </Button>
                      )}
                    </Box>
                  )}

                  {/* Don't show again checkbox - inside body section */}
                  {settings?.allowDismiss && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dontShowAgain}
                          onChange={(e) => setDontShowAgain(e.target.checked)}
                          size="small"
                          sx={{ 
                            color: 'rgba(255,255,255,0.7)',
                            '&.Mui-checked': { color: 'rgba(255,255,255,0.9)' }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
                          Visa inte detta igen
                        </Typography>
                      }
                      sx={{ mt: 2 }}
                    />
                  )}
                </Box>
              )}

              {/* Checkbox overlay when no body section */}
              {!(currentItem.bodyText?.trim() || currentItem.bodyTitle?.trim()) && settings?.allowDismiss && (
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 40, 
                  left: 16, 
                  zIndex: 10,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  borderRadius: 1,
                  px: 1
                }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={dontShowAgain}
                        onChange={(e) => setDontShowAgain(e.target.checked)}
                        size="small"
                        sx={{ 
                          color: 'rgba(255,255,255,0.7)',
                          '&.Mui-checked': { color: 'rgba(255,255,255,0.9)' }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.8rem' }}>
                        Visa inte detta igen
                      </Typography>
                    }
                  />
                </Box>
              )}
            </Box>
          </Fade>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PopupNewsModal;

